import checkApiHealth from './apiCheck';

/**
 * Service for checking API health and availability
 */
class ApiHealthService {
  private static instance: ApiHealthService;
  private readonly apiUrl: string;
  private isAvailableCache: boolean | null = null;
  private lastCheckTime: number = 0;
  private readonly cacheTime: number = 30000; // Cache health check result for 30 seconds

  private constructor() {
    // Get the API URL from environment variables or detect based on current domain
    this.apiUrl = process.env.REACT_APP_API_URL || this.detectApiUrl();
    console.log(`ApiHealthService initialized with API URL: ${this.apiUrl}`);
  }

  /**
   * Detect the API URL based on the current domain
   * @returns The appropriate API URL for the current environment
   */
  private detectApiUrl(): string {
    const hostname = window.location.hostname;
    
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // Development environment
      return 'https://localhost:7045';
    } else if (hostname === 'mindsharecalendar.com' || hostname === 'www.mindsharecalendar.com') {
      // Production environment - use API subdomain
      return 'https://api.mindsharecalendar.com';
    } else {
      // Fallback for other domains (like staging, etc.)
      return `https://api.${hostname}`;
    }
  }

  public static getInstance(): ApiHealthService {
    if (!ApiHealthService.instance) {
      ApiHealthService.instance = new ApiHealthService();
    }
    return ApiHealthService.instance;
  }

  /**
   * Get the base API URL
   * @returns The base API URL
   */
  public getApiUrl(): string {
    return this.apiUrl;
  }

  /**
   * Check if the API is available
   * @param forceCheck Force a new check even if there's a cached result
   * @returns A promise that resolves to true if the API is available, false otherwise
   */
  public async isAvailable(forceCheck: boolean = false): Promise<boolean> {
    const now = Date.now();
    
    // Return cached result if available and not expired
    if (!forceCheck && this.isAvailableCache !== null && now - this.lastCheckTime < this.cacheTime) {
      console.log(`Using cached API health check result: ${this.isAvailableCache ? 'Available' : 'Unavailable'}`);
      return this.isAvailableCache;
    }
    
    try {
      console.log(`Checking API health at ${this.apiUrl}/api/health`);
      
      // Use our direct health checker to avoid circular dependencies
      const isHealthy = await checkApiHealth(this.apiUrl);
      
      this.isAvailableCache = isHealthy;
      this.lastCheckTime = now;
      
      console.log(`API health check result: ${isHealthy ? 'Available' : 'Unavailable'}`);
      return isHealthy;
    } catch (error) {
      console.warn('API health check failed:', error);
      
      this.isAvailableCache = false;
      this.lastCheckTime = now;
      
      console.log(`API health check result: Unavailable`);
      return false;
    }
  }
}

export const apiHealthService = ApiHealthService.getInstance(); 